/**
 * useMyReferral hook
 * React Query wrapper over getMyReferral() (TS-012) for the Refer-a-Friend
 * card (TS-016). Modelled directly on useParentDashboard.ts's useQuery usage.
 *
 * Implements TS-016 (T3); enforces AC-TS016.1 and edge case "code not yet
 * generated" (the backend lazy-generates the code — this hook just surfaces
 * whatever getMyReferral() returns, with no client-side derivation).
 *
 * KB TO-LL-036 (enforcement point: this hook + the card's error/retry
 * render): getMyReferral's errors PROPAGATE by design (see
 * services/referral.ts) — this hook does NOT wrap the query fn in try/catch
 * and does NOT substitute a fallback default on error. The `error` field is
 * surfaced untouched so ReferAFriendCard can render an explicit error state
 * with a working retry, never a silently-empty card.
 * KB TO-LL-003 (enforcement point: the generic type param below): consumes
 * the `MyReferral` type verbatim from '@/types/referral' — code/link/message
 * are not renamed, re-shaped, or given snake_case aliases here.
 */

import { useQuery } from '@tanstack/react-query';
import { getMyReferral } from '@/services/referral';
import type { MyReferral } from '@/types/referral';

export interface UseMyReferralReturn {
  /** The parent's referral code/link/message (undefined during loading or on error). */
  referral: MyReferral | undefined;
  /** True during the initial fetch. */
  isLoading: boolean;
  /** Error object if the fetch failed — never swallowed (TO-LL-036). */
  error: Error | null;
  /** Manually re-triggers the fetch (used by the card's retry button). */
  refetch: () => void;
}

export function useMyReferral(): UseMyReferralReturn {
  const { data, isLoading, error, refetch } = useQuery<MyReferral, Error>({
    queryKey: ['referral', 'me'],
    queryFn: getMyReferral,
    staleTime: 5 * 60 * 1000, // code/link/message rarely change within a session
  });

  return {
    referral: data,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
